package com.swetdot.swetgram.ui.premium

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.swetdot.swetgram.data.premium.PremiumBillingMessage
import com.swetdot.swetgram.domain.PremiumAccessStatus
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.ContentWidth
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.SwetLoading
import com.swetdot.swetgram.ui.design.SwetSecondaryButton
import com.swetdot.swetgram.ui.design.swetLayoutRules
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.premium_active
import swetgram.shared.generated.resources.premium_badge_setting
import swetgram.shared.generated.resources.premium_badge_setting_body
import swetgram.shared.generated.resources.premium_back
import swetgram.shared.generated.resources.premium_billing_acknowledgement_pending
import swetgram.shared.generated.resources.premium_billing_already_active
import swetgram.shared.generated.resources.premium_billing_no_purchase
import swetgram.shared.generated.resources.premium_billing_pending
import swetgram.shared.generated.resources.premium_billing_restored
import swetgram.shared.generated.resources.premium_billing_unavailable
import swetgram.shared.generated.resources.premium_billing_verification_failed
import swetgram.shared.generated.resources.premium_feature_limits
import swetgram.shared.generated.resources.premium_feature_limits_body
import swetgram.shared.generated.resources.premium_feature_organization
import swetgram.shared.generated.resources.premium_feature_organization_body
import swetgram.shared.generated.resources.premium_feature_personalization
import swetgram.shared.generated.resources.premium_feature_personalization_body
import swetgram.shared.generated.resources.premium_feature_profile
import swetgram.shared.generated.resources.premium_feature_profile_body
import swetgram.shared.generated.resources.premium_grace
import swetgram.shared.generated.resources.premium_intro
import swetgram.shared.generated.resources.premium_legal
import swetgram.shared.generated.resources.premium_load_error
import swetgram.shared.generated.resources.premium_monthly
import swetgram.shared.generated.resources.premium_monthly_price
import swetgram.shared.generated.resources.premium_not_eligible
import swetgram.shared.generated.resources.premium_pending
import swetgram.shared.generated.resources.premium_purchase_preparing
import swetgram.shared.generated.resources.premium_restore
import swetgram.shared.generated.resources.premium_retry
import swetgram.shared.generated.resources.premium_sales_closed
import swetgram.shared.generated.resources.premium_start_trial
import swetgram.shared.generated.resources.premium_tagline
import swetgram.shared.generated.resources.premium_title
import swetgram.shared.generated.resources.premium_trial
import swetgram.shared.generated.resources.premium_yearly
import swetgram.shared.generated.resources.premium_yearly_price
import swetgram.shared.generated.resources.premium_yearly_saving

private enum class PremiumBillingChoice { MONTHLY, YEARLY }

@Composable
fun PremiumScreen(
    state: PremiumUiState,
    onBack: () -> Unit,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier,
    onDismissError: () -> Unit = {},
    onStartPurchase: ((basePlanId: String) -> Unit)? = null,
    onRestorePurchases: (() -> Unit)? = null,
    monthlyPrice: String? = null,
    yearlyPrice: String? = null,
    billingReady: Boolean = false,
    billingProcessing: Boolean = false,
    billingMessage: PremiumBillingMessage? = null,
    onBadgeVisibleChange: (Boolean) -> Unit = {},
) {
    var choice by remember { mutableStateOf(PremiumBillingChoice.YEARLY) }
    val entitlement = state.entitlement
    val canStart = entitlement?.let { it.eligible && it.salesEnabled && !it.active } == true &&
        onStartPurchase != null && billingReady && !billingProcessing
    val purchaseLabel = when {
        entitlement?.active == true -> stringResource(Res.string.premium_active)
        billingProcessing -> stringResource(Res.string.premium_purchase_preparing)
        else -> stringResource(Res.string.premium_start_trial)
    }

    Column(modifier.fillMaxSize().background(PremiumPalette.Background)) {
        PremiumTopBar(
            title = stringResource(Res.string.premium_title),
            backLabel = stringResource(Res.string.premium_back),
            onBack = onBack,
        )

        if (state.error != null && entitlement != null) {
            SwetErrorBanner(
                message = stringResource(Res.string.premium_load_error),
                onRetry = onRetry,
                onDismiss = onDismissError,
            )
        }

        if (entitlement == null) {
            if (!state.loadedOnce) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    SwetLoading()
                }
            } else {
                SwetEmptyState(
                    title = stringResource(Res.string.premium_title),
                    body = stringResource(Res.string.premium_load_error),
                    actionText = stringResource(Res.string.premium_retry),
                    onAction = onRetry,
                    modifier = Modifier.fillMaxSize().navigationBarsPadding(),
                )
            }
            return@Column
        }

        BoxWithConstraints(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.TopCenter,
        ) {
            val useTwoColumns = swetLayoutRules(
                widthDp = maxWidth.value.toInt().coerceAtLeast(1),
                heightDp = maxHeight.value.toInt().coerceAtLeast(1),
            ).useTwoColumns
            Column(
                modifier = Modifier
                    .widthIn(max = ContentWidth.premium)
                    .fillMaxWidth()
                    .navigationBarsPadding(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .verticalScroll(rememberScrollState())
                        .padding(horizontal = PremiumHorizontalMargin, vertical = 8.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                SubscriptionHero(
                    headline = stringResource(Res.string.premium_tagline),
                    description = stringResource(Res.string.premium_intro),
                )

                Spacer(Modifier.height(8.dp))
                if (useTwoColumns) Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    SubscriptionPlanCard(
                        title = stringResource(Res.string.premium_monthly),
                        price = monthlyPrice ?: stringResource(Res.string.premium_monthly_price),
                        selected = choice == PremiumBillingChoice.MONTHLY,
                        onClick = { choice = PremiumBillingChoice.MONTHLY },
                        modifier = Modifier.weight(1f),
                    )
                    SubscriptionPlanCard(
                        title = stringResource(Res.string.premium_yearly),
                        price = yearlyPrice ?: stringResource(Res.string.premium_yearly_price),
                        saving = stringResource(Res.string.premium_yearly_saving),
                        selected = choice == PremiumBillingChoice.YEARLY,
                        onClick = { choice = PremiumBillingChoice.YEARLY },
                        modifier = Modifier.weight(1f),
                    )
                } else Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    SubscriptionPlanCard(
                        title = stringResource(Res.string.premium_monthly),
                        price = monthlyPrice ?: stringResource(Res.string.premium_monthly_price),
                        selected = choice == PremiumBillingChoice.MONTHLY,
                        onClick = { choice = PremiumBillingChoice.MONTHLY },
                        modifier = Modifier.fillMaxWidth(),
                    )
                    SubscriptionPlanCard(
                        title = stringResource(Res.string.premium_yearly),
                        price = yearlyPrice ?: stringResource(Res.string.premium_yearly_price),
                        saving = stringResource(Res.string.premium_yearly_saving),
                        selected = choice == PremiumBillingChoice.YEARLY,
                        onClick = { choice = PremiumBillingChoice.YEARLY },
                        modifier = Modifier.fillMaxWidth(),
                    )
                }

                Spacer(Modifier.height(4.dp))
                Text(
                    text = stringResource(Res.string.premium_trial),
                    style = MaterialTheme.typography.bodySmall.copy(fontSize = 11.sp, lineHeight = 14.sp),
                    color = PremiumPalette.Secondary,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth(),
                )

                entitlement.let { access ->
                    val status = statusText(access.status)
                    if (status != null || access.active) {
                        Spacer(Modifier.height(8.dp))
                        PremiumSurfaceCard {
                            Column(Modifier.fillMaxWidth()) {
                                if (status != null) {
                                    Text(
                                        text = stringResource(status),
                                        style = MaterialTheme.typography.titleMedium,
                                        color = PremiumPalette.Primary,
                                        fontWeight = FontWeight.SemiBold,
                                    )
                                }
                                if (access.active) {
                                    if (status != null) Spacer(Modifier.height(6.dp))
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                                    ) {
                                        Column(Modifier.weight(1f)) {
                                            Text(
                                                text = stringResource(Res.string.premium_badge_setting),
                                                style = MaterialTheme.typography.titleMedium,
                                                color = PremiumPalette.Primary,
                                            )
                                            Spacer(Modifier.height(2.dp))
                                            Text(
                                                text = stringResource(Res.string.premium_badge_setting_body),
                                                style = MaterialTheme.typography.bodySmall,
                                                color = PremiumPalette.Secondary,
                                            )
                                        }
                                        Switch(
                                            checked = access.badgeVisible,
                                            onCheckedChange = if (state.savingPreference) null else onBadgeVisibleChange,
                                            enabled = !state.savingPreference,
                                            colors = SwitchDefaults.colors(
                                                checkedThumbColor = PremiumPalette.Inverse,
                                                checkedTrackColor = Color.White,
                                                uncheckedThumbColor = PremiumPalette.Secondary,
                                                uncheckedTrackColor = PremiumPalette.IconSurface,
                                                uncheckedBorderColor = Color.White.copy(alpha = 0.18f),
                                            ),
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                Spacer(Modifier.height(6.dp))
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    if (useTwoColumns) Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        PremiumBenefitCard(
                            icon = SwetIcons.User,
                            title = stringResource(Res.string.premium_feature_personalization),
                            description = stringResource(Res.string.premium_feature_personalization_body),
                            modifier = Modifier.weight(1f),
                        )
                        PremiumBenefitCard(
                            icon = SwetIcons.Folder,
                            title = stringResource(Res.string.premium_feature_organization),
                            description = stringResource(Res.string.premium_feature_organization_body),
                            modifier = Modifier.weight(1f),
                        )
                    } else {
                        PremiumBenefitCard(
                            icon = SwetIcons.User,
                            title = stringResource(Res.string.premium_feature_personalization),
                            description = stringResource(Res.string.premium_feature_personalization_body),
                        )
                        PremiumBenefitCard(
                            icon = SwetIcons.Folder,
                            title = stringResource(Res.string.premium_feature_organization),
                            description = stringResource(Res.string.premium_feature_organization_body),
                        )
                    }
                    if (useTwoColumns) Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        PremiumBenefitCard(
                            icon = SwetIcons.User,
                            title = stringResource(Res.string.premium_feature_profile),
                            description = stringResource(Res.string.premium_feature_profile_body),
                            modifier = Modifier.weight(1f),
                        )
                        PremiumBenefitCard(
                            icon = SwetIcons.Document,
                            title = stringResource(Res.string.premium_feature_limits),
                            description = stringResource(Res.string.premium_feature_limits_body),
                            modifier = Modifier.weight(1f),
                        )
                    } else {
                        PremiumBenefitCard(
                            icon = SwetIcons.User,
                            title = stringResource(Res.string.premium_feature_profile),
                            description = stringResource(Res.string.premium_feature_profile_body),
                        )
                        PremiumBenefitCard(
                            icon = SwetIcons.Document,
                            title = stringResource(Res.string.premium_feature_limits),
                            description = stringResource(Res.string.premium_feature_limits_body),
                        )
                    }
                }

                if (!entitlement.eligible) {
                    Spacer(Modifier.height(6.dp))
                    SupportingNotice(stringResource(Res.string.premium_not_eligible))
                } else if (!entitlement.salesEnabled) {
                    Spacer(Modifier.height(6.dp))
                    SupportingNotice(stringResource(Res.string.premium_sales_closed))
                }

                if (billingMessage != null) {
                    Spacer(Modifier.height(6.dp))
                    SupportingNotice(
                        stringResource(
                            billingMessageResource(
                                message = billingMessage,
                                premiumAlreadyActive = entitlement.active,
                            ),
                        ),
                    )
                }

                if (onRestorePurchases != null && !entitlement.active) {
                    Spacer(Modifier.height(4.dp))
                    SwetSecondaryButton(
                        text = stringResource(Res.string.premium_restore),
                        onClick = onRestorePurchases,
                        modifier = Modifier.fillMaxWidth(0.7f),
                    )
                }

                Spacer(Modifier.height(4.dp))
                Text(
                    text = stringResource(Res.string.premium_legal),
                    style = MaterialTheme.typography.bodySmall.copy(fontSize = 10.sp, lineHeight = 13.sp),
                    color = PremiumPalette.Tertiary,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth(),
                )
                Spacer(Modifier.height(4.dp))
                }

                Surface(
                    color = PremiumPalette.Background,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    SubscriptionCta(
                        text = purchaseLabel,
                        enabled = canStart,
                        loading = billingProcessing,
                        onClick = {
                            onStartPurchase?.invoke(
                                if (choice == PremiumBillingChoice.YEARLY) "yearly" else "monthly",
                            )
                        },
                        modifier = Modifier.padding(
                            horizontal = PremiumHorizontalMargin,
                            vertical = 8.dp,
                        ),
                    )
                }
            }
        }
    }
}

@Composable
private fun SupportingNotice(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodySmall.copy(fontSize = 11.sp, lineHeight = 14.sp),
        color = PremiumPalette.Secondary,
        textAlign = TextAlign.Center,
        modifier = Modifier.fillMaxWidth(),
    )
}

private fun statusText(status: PremiumAccessStatus): StringResource? = when (status) {
    PremiumAccessStatus.ACTIVE -> Res.string.premium_active
    PremiumAccessStatus.GRACE_PERIOD -> Res.string.premium_grace
    PremiumAccessStatus.PENDING -> Res.string.premium_pending
    else -> null
}

internal fun billingMessageResource(
    message: PremiumBillingMessage,
    premiumAlreadyActive: Boolean,
): StringResource = when (message) {
    PremiumBillingMessage.UNAVAILABLE -> Res.string.premium_billing_unavailable
    PremiumBillingMessage.NO_PURCHASE_FOUND -> if (premiumAlreadyActive) {
        Res.string.premium_billing_already_active
    } else Res.string.premium_billing_no_purchase
    PremiumBillingMessage.PAYMENT_PENDING -> Res.string.premium_billing_pending
    PremiumBillingMessage.VERIFICATION_FAILED -> Res.string.premium_billing_verification_failed
    PremiumBillingMessage.ACKNOWLEDGEMENT_PENDING -> Res.string.premium_billing_acknowledgement_pending
    PremiumBillingMessage.RESTORED -> Res.string.premium_billing_restored
}
